import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useFinance } from '@/hooks/useFinance';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { formatMoney } from '@/lib/finance';
import { reserveDocNumber } from '@/lib/invoiceNumbering';

interface OcrLine {
  description: string;
  quantity: number;
  unit_price: number;
}

interface OcrInvoice {
  customer_name: string;
  customer_ice: string;
  customer_phone: string;
  customer_email: string;
  customer_address: string;
  number: string;
  issue_date: string;
  due_date: string;
  currency: string;
  tax_rate: number;
  description: string;
  total: number;
  items: OcrLine[];
}

interface InvoiceImportModalProps {
  onClose: () => void;
  onDone: (message: string) => void;
}

const ACCEPTED = 'image/jpeg,image/png,image/webp,application/pdf';

/**
 * Import d'une facture fournisseur par photo ou PDF.
 * Le document est lu par l'IA (OCR), les champs sont pré-remplis et
 * **modifiables** avant création : rien n'est enregistré à l'aveugle.
 */
export default function InvoiceImportModal({ onClose, onDone }: InvoiceImportModalProps) {
  const finance = useFinance();
  const company = useCompanyProfile();
  const { currency, invoices, settings, merchantId } = finance;

  const [step, setStep] = useState<'upload' | 'scanning' | 'preview'>('upload');
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [draft, setDraft] = useState<OcrInvoice | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const readFile = useCallback(async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      setError('Le fichier dépasse 10 Mo. Compressez la photo ou envoyez une page seulement.');
      return;
    }
    setError(null);
    setFileName(file.name);
    setStep('scanning');

    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^,]+,/, ''));
        reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
        reader.readAsDataURL(file);
      });

      const { data, error: fnError } = await supabase.functions.invoke('invoice-ocr', {
        body: { fileBase64: base64, mimeType: file.type || 'image/jpeg' },
      });
      if (fnError) throw new Error(fnError.message || 'Analyse impossible');

      const payload = data as { invoice?: OcrInvoice; provider?: string; error?: string } | null;
      if (payload?.error) throw new Error(payload.error);
      if (!payload?.invoice) throw new Error("Le document n'a pas pu être analysé.");

      const today = new Date().toISOString().slice(0, 10);
      const items = (payload.invoice.items || []).filter((it) => it.description || it.unit_price);
      setDraft({
        ...payload.invoice,
        issue_date: payload.invoice.issue_date || today,
        tax_rate: company.profile.vat_subject ? Number(payload.invoice.tax_rate) || 0 : 0,
        items: items.length > 0 ? items : [{ description: payload.invoice.description || 'Achat', quantity: 1, unit_price: payload.invoice.total || 0 }],
      });
      setProvider(payload.provider || null);
      setStep('preview');
    } catch (e) {
      setError(e instanceof Error ? e.message : "L'analyse a échoué.");
      setStep('upload');
    }
  }, [company.profile.vat_subject]);

  const updateLine = (index: number, patch: Partial<OcrLine>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((it, i) => (i === index ? { ...it, ...patch } : it));
      return { ...prev, items };
    });
  };

  const removeLine = (index: number) => {
    setDraft((prev) => (prev ? { ...prev, items: prev.items.filter((_, i) => i !== index) } : prev));
  };

  const addLine = () => {
    setDraft((prev) => (prev ? { ...prev, items: [...prev.items, { description: '', quantity: 1, unit_price: 0 }] } : prev));
  };

  const subtotal = (draft?.items || []).reduce(
    (s, it) => s + Math.max(0, (Number(it.quantity) || 0) * (Number(it.unit_price) || 0)),
    0,
  );
  const taxAmount = (subtotal * (Number(draft?.tax_rate) || 0)) / 100;
  const total = subtotal + taxAmount;

  const create = async () => {
    if (!draft || saving) return;
    if (!draft.customer_name.trim()) {
      setError('Le nom du client / fournisseur est obligatoire.');
      return;
    }
    if (draft.items.length === 0) {
      setError('Ajoutez au moins une ligne de facture.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const number = await reserveDocNumber({
        docType: 'invoice',
        merchantId,
        profile: company.profile,
        invoices,
        invoicePrefix: settings?.invoice_prefix || company.profile.invoice_prefix,
        reserveInvoiceNumber: company.reserveInvoiceNumber,
      });

      await finance.createInvoice({
        number,
        doc_type: 'invoice',
        customer_name: draft.customer_name.trim(),
        customer_phone: draft.customer_phone || null,
        customer_email: draft.customer_email || null,
        customer_address: draft.customer_address || null,
        customer_ice: draft.customer_ice || null,
        issue_date: draft.issue_date || new Date().toISOString().slice(0, 10),
        due_date: draft.due_date || null,
        reference: draft.number || fileName || null,
        tax_rate: Number(draft.tax_rate) || 0,
        notes: `Importée automatiquement par OCR depuis « ${fileName} ».${draft.description ? ` ${draft.description}` : ''}`,
        status: 'sent',
        items: draft.items.map((it) => ({
          description: it.description || 'Ligne importée',
          quantity: Math.max(1, Number(it.quantity) || 1),
          unit_price: Math.max(0, Number(it.unit_price) || 0),
          discount: 0,
          item_type: 'custom',
          product_id: null,
          subtotal: Math.max(0, (Number(it.quantity) || 1) * (Number(it.unit_price) || 0)),
        })),
      });

      onDone(`Facture ${number} créée depuis « ${fileName} » (${formatMoney(total, draft.currency || currency)}).`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose}></div>
      <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none">
        <div className="pointer-events-auto w-full sm:max-w-2xl max-h-[92vh] bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 flex flex-col overflow-hidden">
          {/* En-tête */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-background-200/70 flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-scan-2-line"></i>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-bold text-foreground-950">Importer une facture</h3>
              <p className="text-[11px] text-foreground-500 truncate">
                Photo ou PDF · lecture automatique par IA
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
              title="Fermer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {error && (
              <div className="mb-3 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50 text-xs text-red-600">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                <span className="flex-1 break-words">{error}</span>
              </div>
            )}

            {/* Étape 1 : dépôt du fichier */}
            {step === 'upload' && (
              <div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 py-12 rounded-xl border-2 border-dashed border-background-300/70 hover:border-accent-400 hover:bg-accent-50/40 transition-colors cursor-pointer"
                >
                  <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-camera-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-800">Prendre une photo ou choisir un fichier</p>
                  <p className="text-[11px] text-foreground-500 max-w-xs text-center">
                    JPG, PNG, WebP ou PDF · 10 Mo maximum. Le document reste lisible et modifiable avant enregistrement.
                  </p>
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPTED}
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void readFile(file);
                    e.target.value = '';
                  }}
                />
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { icon: 'ri-sparkling-line', title: 'Champs extraits', hint: 'Client, ICE, dates, TVA' },
                    { icon: 'ri-list-check', title: 'Lignes reconnues', hint: 'Quantité et prix unitaire' },
                    { icon: 'ri-edit-line', title: 'Tout modifiable', hint: 'Corrigez avant de valider' },
                  ].map((f) => (
                    <div key={f.title} className="flex items-start gap-2 p-2.5 rounded-lg bg-background-100/70">
                      <i className={`${f.icon} text-accent-600 mt-0.5`}></i>
                      <div>
                        <p className="text-[11px] font-semibold text-foreground-800">{f.title}</p>
                        <p className="text-[10px] text-foreground-500">{f.hint}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Étape 2 : analyse */}
            {step === 'scanning' && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-4">
                  <i className="ri-loader-4-line animate-spin text-2xl"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-900">Lecture de « {fileName} »…</p>
                <p className="text-xs text-foreground-500 mt-1 max-w-xs">
                  L&apos;IA extrait le client, les dates, la TVA et les lignes de la facture.
                </p>
              </div>
            )}

            {/* Étape 3 : vérification */}
            {step === 'preview' && draft && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-[11px] text-accent-800 bg-accent-50 px-3 py-2 rounded-lg">
                  <i className="ri-checkbox-circle-line"></i>
                  Document lu{provider ? ` par ${provider}` : ''}. Vérifiez les informations puis validez.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Client / fournisseur *</label>
                    <input
                      value={draft.customer_name}
                      onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-300"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1">ICE</label>
                    <input
                      value={draft.customer_ice}
                      onChange={(e) => setDraft({ ...draft, customer_ice: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-300"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1">N° de facture d&apos;origine</label>
                    <input
                      value={draft.number}
                      onChange={(e) => setDraft({ ...draft, number: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-300"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Date d&apos;émission</label>
                    <input
                      type="date"
                      value={draft.issue_date}
                      onChange={(e) => setDraft({ ...draft, issue_date: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-300"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Échéance</label>
                    <input
                      type="date"
                      value={draft.due_date}
                      onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-300"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1">TVA (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={30}
                      value={draft.tax_rate}
                      disabled={!company.profile.vat_subject}
                      onChange={(e) => setDraft({ ...draft, tax_rate: Number(e.target.value) })}
                      className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-300 disabled:opacity-60"
                    />
                    {!company.profile.vat_subject && (
                      <p className="text-[10px] text-foreground-400 mt-1">Non assujetti : TVA forcée à 0 %.</p>
                    )}
                  </div>
                </div>

                {/* Lignes */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[11px] font-semibold text-foreground-600">Lignes de la facture</label>
                    <button
                      type="button"
                      onClick={addLine}
                      className="flex items-center gap-1 text-[11px] font-medium text-primary-700 hover:bg-primary-50 px-2 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i>Ajouter
                    </button>
                  </div>
                  <div className="rounded-lg border border-background-200/70 overflow-hidden">
                    <div className="grid grid-cols-12 gap-1 px-2 py-1.5 bg-background-100 text-[10px] font-semibold text-foreground-500">
                      <span className="col-span-6">Désignation</span>
                      <span className="col-span-2 text-center">Qté</span>
                      <span className="col-span-3 text-right">P.U. HT</span>
                      <span className="col-span-1"></span>
                    </div>
                    <div className="divide-y divide-background-200/70 max-h-56 overflow-y-auto">
                      {draft.items.map((it, i) => (
                        <div key={i} className="grid grid-cols-12 gap-1 items-center px-2 py-1.5">
                          <input
                            value={it.description}
                            onChange={(e) => updateLine(i, { description: e.target.value })}
                            className="col-span-6 min-w-0 px-2 py-1.5 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-accent-300"
                          />
                          <input
                            type="number"
                            min={1}
                            value={it.quantity}
                            onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })}
                            className="col-span-2 w-full px-1.5 py-1.5 rounded-md border border-background-200/70 bg-background-50 text-xs text-center text-foreground-900 focus:outline-none focus:border-accent-300"
                          />
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={it.unit_price}
                            onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) })}
                            className="col-span-3 w-full px-1.5 py-1.5 rounded-md border border-background-200/70 bg-background-50 text-xs text-right text-foreground-900 focus:outline-none focus:border-accent-300"
                          />
                          <button
                            type="button"
                            onClick={() => removeLine(i)}
                            className="col-span-1 w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                            title="Retirer la ligne"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Totaux */}
                <div className="flex flex-col items-end gap-1 text-sm">
                  <div className="flex items-center justify-between w-full sm:w-64">
                    <span className="text-foreground-500">Total HT</span>
                    <span className="font-medium text-foreground-800">{formatMoney(subtotal, draft.currency || currency)}</span>
                  </div>
                  {Number(draft.tax_rate) > 0 && (
                    <div className="flex items-center justify-between w-full sm:w-64">
                      <span className="text-foreground-500">TVA {draft.tax_rate} %</span>
                      <span className="font-medium text-foreground-800">{formatMoney(taxAmount, draft.currency || currency)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between w-full sm:w-64 pt-1 border-t border-background-200/70">
                    <span className="font-semibold text-foreground-900">Total TTC</span>
                    <span className="font-bold text-foreground-950">{formatMoney(total, draft.currency || currency)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Pied */}
          {step === 'preview' && (
            <div className="px-4 py-3 border-t border-background-200/70 flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => { setStep('upload'); setDraft(null); }}
                className="flex-1 py-2.5 rounded-full border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Changer de document
              </button>
              <button
                type="button"
                onClick={create}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving ? <><i className="ri-loader-4-line animate-spin"></i>Création…</> : <><i className="ri-check-double-line"></i>Créer la facture</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}